import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { InquiryItem } from 'src/hooks/useInquiriesRealtime';
import { ChatConversationItem } from 'src/api/mutations/useGetTrainerMessages';
import {
  ChatMessage,
  parseFirestoreDate,
} from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/types';
import { ReactComponent as EmptyInquiriesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/empty-inquiries.svg';
import { ReactComponent as SupportHeadsetIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-support-headset.svg';
import { sessionStore } from 'src/store/sessionStore';
import { auth } from 'src/config/firebase';

interface InquiriesListProps {
  inquiries: InquiryItem[];
  isLoading: boolean;
  conversations: ChatConversationItem[];
  activeChatId?: string;
  activeInquiryId?: string;
}

export const InquiriesList: FC<InquiriesListProps> = ({
  inquiries,
  isLoading,
  conversations,
  activeChatId: _activeChatId,
  activeInquiryId,
}) => {
  const navigate = useNavigate();
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerUid = currentUser?.uid || auth.currentUser?.uid || '';

  const getClientDetails = (chatId: string) => {
    return conversations.find((c) => c.chatId === chatId)?.client;
  };

  const getInitials = (name: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const formatInquiryTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = parseFirestoreDate(timestamp);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) {
      return `Sent at ${timeStr}`;
    }
    if (isYesterday) {
      return 'Yesterday';
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getAttachmentSummary = (message: ChatMessage): string => {
    const attachments = message.attachments || [];
    const mediaUrl = message.media_url;

    if (attachments.length === 0 && !mediaUrl) {
      return 'no attachment';
    }

    if (attachments.length > 0) {
      const videoCount = attachments.filter(
        (a) =>
          a.type === 'video' ||
          a.mime_type?.startsWith('video/') ||
          /\.(mp4|mov|avi|webm|mkv)$/i.test(a.file_name || a.url || ''),
      ).length;

      const imageCount = attachments.filter(
        (a) =>
          a.type === 'image' ||
          a.mime_type?.startsWith('image/') ||
          /\.(jpe?g|png|webp|gif|avif|heic|heif|svg)$/i.test(a.file_name || a.url || ''),
      ).length;

      if (videoCount > 0 && imageCount === 0 && videoCount === attachments.length) {
        return `${videoCount} video${videoCount > 1 ? 's' : ''} attached`;
      }
      if (imageCount > 0 && videoCount === 0 && imageCount === attachments.length) {
        return `${imageCount} photo${imageCount > 1 ? 's' : ''} attached`;
      }
      const total = attachments.length;
      return `${total} attachment${total > 1 ? 's' : ''}`;
    }

    if (mediaUrl) {
      const isVideo =
        message.message_type === 'video' ||
        /\.(mp4|mov|avi|webm|mkv)$/i.test(message.file_name || mediaUrl);
      if (isVideo) return '1 video attached';
      const isImage =
        message.message_type === 'image' ||
        /\.(jpe?g|png|webp|gif|avif|heic|heif|svg)$/i.test(message.file_name || mediaUrl);
      if (isImage) return '1 photo attached';
      return '1 attachment';
    }

    return 'no attachment';
  };

  const renderStatusBadge = (message: ChatMessage, isSupport?: boolean) => {
    if (message.isResolve === true || message.status === 'resolved') {
      return <div className="inquiry-status-pill status-resolved">Resolved</div>;
    }
    if (message.status === 'reopened') {
      return (
        <div className="inquiry-status-pill status-reopened">
          <span className="status-dot" />
          Reopened
        </div>
      );
    }
    if (message.status === 'replied') {
      return <div className="inquiry-status-pill status-replied">Replied</div>;
    }
    const isSentByMe =
      message.sender_role === 'trainer' ||
      message.sender_uid === trainerUid ||
      message.send_uid === trainerUid;

    if (!isSentByMe && (!message.is_read || message.status === 'new')) {
      return (
        <div className="inquiry-status-pill status-new">
          <span className="status-dot" />
          New
        </div>
      );
    }
    if (isSupport) {
      return null;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          padding: '20px 0',
          minHeight: 'auto',
          textAlign: 'center',
          width: '100%',
          color: '#A0A0A0',
        }}
      >
        <Spin
          indicator={<LoadingOutlined className="loading-icon" spin style={{ color: '#E1FF00' }} />}
        />
        <div style={{ marginTop: '8px' }}>Loading inquiries...</div>
      </div>
    );
  }

  if (inquiries.length === 0) {
    return (
      <div className="dashboard-empty-state">
        <div className="empty-icon">
          <EmptyInquiriesIcon />
        </div>
        <h2>No open inquiries found</h2>
        <p>You have no pending client inquiries at the moment.</p>
      </div>
    );
  }

  return (
    <div className="messages-list-container" id="scrollableInquiriesList">
      {inquiries.map((item) => {
        const client = getClientDetails(item.chatId);
        const isSupport = Boolean(
          item.message.is_support ||
          item.chatId.startsWith('support_') ||
          item.message.sender_name === 'miniPT Support' ||
          item.message.receive_name === 'miniPT Support',
        );
        const clientName = isSupport
          ? 'miniPT Support'
          : client?.fullName || item.message.sender_name || 'Client';
        const clientAvatar = isSupport
          ? ''
          : client?.profilePicture || item.message.sender_avatar || '';

        const categoryLabel =
          item.message.inquiryType ||
          item.message.topic ||
          item.message.category ||
          item.message.inquiry_category ||
          (isSupport ? 'Billing & Subscription' : 'General');
        const attachmentText = getAttachmentSummary(item.message);
        const lastMessage = isSupport
          ? (item.message.body && item.message.body !== 'Inquiry sent' ? item.message.body : '') ||
            item.message.message ||
            item.message.subject ||
            'Support inquiry'
          : item.message.inquirySubject ||
            item.message.subject ||
            item.message.inquiryDetail ||
            item.message.message ||
            (item.message.body && item.message.body !== 'Inquiry sent' ? item.message.body : '') ||
            item.message.inquiry_text ||
            'Inquiry sent';

        const isSelected = Boolean(activeInquiryId) && item.message.id === activeInquiryId;

        return (
          <div
            key={item.message.id}
            className={`message-item inquiry-item-card ${isSelected ? 'active' : ''}`}
            onClick={() =>
              navigate(
                `/onboarding-trainer/dashboard/messages/${item.chatId}?tab=inquiries&inquiryId=${item.message.id}`,
              )
            }
          >
            <div
              className={`message-avatar ${clientAvatar ? 'has-image' : ''} ${isSupport ? 'is-support-avatar' : ''}`}
              style={{
                backgroundImage: clientAvatar ? `url(${clientAvatar})` : 'none',
              }}
            >
              {isSupport ? (
                <SupportHeadsetIcon className="support-headset-icon" />
              ) : (
                !clientAvatar && getInitials(clientName)
              )}
            </div>

            <div className="inquiry-item-content">
              <div className="inquiry-item-top-row">
                <h3 className="inquiry-client-name">{clientName}</h3>
                {renderStatusBadge(item.message, isSupport)}
              </div>
              <span className="inquiry-sent-time">{formatInquiryTime(item.message.send_time)}</span>
              {!isSupport && (
                <div className="inquiry-item-sub-row">
                  <span
                    className="inquiry-meta-category-attachment"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {categoryLabel} · {attachmentText}
                  </span>
                </div>
              )}
              {Boolean(lastMessage?.trim()) && (
                <div className="inquiry-item-last-message">
                  <p
                    className={
                      !item.message.is_read &&
                      item.message.sender_role !== 'trainer' &&
                      item.message.sender_uid !== trainerUid &&
                      item.message.send_uid !== trainerUid
                        ? 'unread-text'
                        : ''
                    }
                  >
                    {lastMessage}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
